import { useState, useCallback } from "react"
import { BarChart3, Globe, Link, TrendingUp, DollarSign, Target, Activity, FolderOpen, AlertCircle, Plus, Eye, Edit, Users, X, Check, Calendar, Clock, GripVertical, ArrowRight, FileText, UserPlus, UserCheck, Building2, Tag, ClipboardCheck, UserCog, Shield } from "lucide-react"
import { Layout } from "@/shared/components"
import {
  FMOVendorIntakeForms,
  FMOLoadBalancer,
  FMOGlobalSalesReports,
  FMOGlobalPayReports,
  FMOVendorFormAssignment,
  CampaignSelector,
  ViewDashboard,
  AddCampaign,
  EditCampaign,
  EditIntakeLinks,
  AssignIntakeLinks,
  LoadBalanceIntakeLinks,
  ListIdManager,
} from "@/features/fmo"
import ApplicationDataLogs from "@/features/fmo/components/ApplicationDataLogs"
import {
  SalesReportManager,
  PayReportManager,
  CommissionRateManager,
  OverrideRateManager,
  AgentManagement as AgentStatusManager,
  AgentAssignmentManager,
  OrganizationManager,
  HRAccountManagement,
} from "@/features/hr"
import { Campaign, useCampaign } from "@/features/campaigns"
import { QualityControl } from "@/features/quality-control"
import { DashboardContainer } from "@/features/dashboard"
import { ReferralAnalytics } from "@/features/referral-analytics"

export default function FMOCEODashboard() {
  const { selectedCampaign, setSelectedCampaign, isCampaignSelected } = useCampaign()

  // Get initial tab from URL hash or default to view-dashboard
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabs = ["campaign-selector", "add-campaign", "view-dashboard", "edit-campaign", "edit-intake-links", "assign-intake-links", "load-balance-intake-links", "hr-sales-reports", "hr-pay-reports", "hr-agent-assignment", "hr-commission-rates", "hr-override-rates", "hr-agent-status", "hr-organizations", "vendor-intake", "form-assignments", "load-balancer", "global-sales", "global-pay", "organizations", "list-id-management", "quality-control", "hr-account-management", "referrals", "application-data-logs"]
    return validTabs.includes(hash) ? hash : "view-dashboard"
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)


  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Memoize the onCampaignUpdated callback to prevent unnecessary re-renders
  const handleCampaignUpdated = useCallback((updatedCampaign: any) => {
    setSelectedCampaign(updatedCampaign)
  }, [setSelectedCampaign])

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    // Update URL hash without triggering page reload
    window.history.replaceState(null, '', `#${tabId}`)
  }


  const navigationItems = [
    { id: "view-dashboard", label: "View Campaign", icon: Eye },
    { id: "add-campaign", label: "Add New Campaign", icon: Plus },
    { id: "hr-account-management", label: "HR Account Management", icon: UserCog },
    { id: "campaign-selector", label: "Campaign Selector", icon: FolderOpen },
    { id: "edit-campaign", label: "Edit Campaign", icon: Edit },
    { id: "edit-intake-links", label: "Edit Intake Links", icon: Link },
    { id: "assign-intake-links", label: "Assign Intake Links", icon: ArrowRight },
    { id: "load-balance-intake-links", label: "Load Balance Intake Links", icon: BarChart3 },
    { id: "sales-reports", label: "Sales Reports", icon: FileText },
    { id: "pay-reports", label: "Pay Reports", icon: DollarSign },
    { id: "commission-rates", label: "Commission Rates", icon: Target },
    { id: "override-rates", label: "Override Rates", icon: Target },
    { id: "agent-status", label: "Agent Status", icon: UserCheck },
    { id: "agent-assignment", label: "Agent Assignment", icon: UserPlus },
    { id: "organizations", label: "Organizations", icon: Building2 },
    { id: "list-id-management", label: "List ID Management", icon: Tag },
    { id: "quality-control", label: "Quality Control", icon: ClipboardCheck },
    { id: "referrals", label: "Referral Analytics", icon: UserPlus },
    { id: "application-data-logs", label: "Application Data Logs", icon: Shield },
  ]

  const handleCampaignSelect = (campaign: Campaign | null) => {
    setSelectedCampaign(campaign)
  }

  // Campaign selection requirement component
  const CampaignSelectionRequired = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center max-w-md">
        <div className="bg-yellow-50 border border-yellow-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-yellow-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Selection Required</h3>
        <p className="text-gray-600 mb-4">
          Please select a campaign from the Campaign Selector tab to view and manage campaign-specific data. You can also use the "View Campaign" tab to see overall campaign statistics.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => handleTabChange("campaign-selector")}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Go to Campaign Selector
          </button>
          <button
            onClick={() => handleTabChange("view-dashboard")}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            View Campaign Statistics
          </button>
        </div>
      </div>
    </div>
  )

  const renderContent = () => {
    // Dashboard is always accessible
    if (activeTab === "dashboard") {
      return <DashboardContainer />
    }

    // Campaign selector, add campaign, view dashboard, and HR Account Management are always accessible
    if (activeTab === "campaign-selector") {
      return (
        <CampaignSelector
          onCampaignSelect={handleCampaignSelect}
          selectedCampaignId={selectedCampaign?.id}
          showActions={true}
        />
      )
    }

    if (activeTab === "add-campaign") {
      return (
        <AddCampaign
          onCancel={() => handleTabChange("campaign-selector")}
          onTabChange={handleTabChange}
        />
      )
    }

    if (activeTab === "view-dashboard") {
      return <ViewDashboard campaignName={selectedCampaign?.name} />
    }

    // HR Account Management and Referrals accessible without campaign selection
    if (activeTab === "hr-account-management") {
      return <HRAccountManagement onError={(error) => console.error(error)} onSuccess={(message) => console.log(message)} />
    }

    if (activeTab === "referrals") {
      return <ReferralAnalytics />
    }

    // Application Data Logs — FMO only, no campaign required
    if (activeTab === "application-data-logs") {
      return <ApplicationDataLogs />
    }

    // Quality control requires campaign selection
    if (activeTab === "quality-control") {
      if (!isCampaignSelected) {
        return <CampaignSelectionRequired />
      }
      return <QualityControl campaignId={selectedCampaign?.id} campaignName={selectedCampaign?.name} />
    }

    // All other tabs require campaign selection
    if (!isCampaignSelected) {
      return <CampaignSelectionRequired />
    }

    // Render campaign-specific content
    switch (activeTab) {
      case "edit-campaign":
        return <EditCampaign
          campaignId={selectedCampaign?.id}
          campaignName={selectedCampaign?.name}
          onCampaignUpdated={handleCampaignUpdated}
        />
      case "edit-intake-links":
        return <EditIntakeLinks campaignName={selectedCampaign?.name} />
      case "assign-intake-links":
        return <AssignIntakeLinks campaignName={selectedCampaign?.name} />
      case "load-balance-intake-links":
        return <LoadBalanceIntakeLinks campaignName={selectedCampaign?.name} />
      case "sales-reports":
        return <SalesReportManager onError={(error) => console.error(error)} />
      case "pay-reports":
        return <PayReportManager onError={(error) => console.error(error)} />
      case "agent-assignment":
        return <AgentAssignmentManager onError={(error) => console.error(error)} />
      case "commission-rates":
        return <CommissionRateManager onError={(error) => console.error(error)} />
      case "override-rates":
        return <OverrideRateManager onError={(error) => console.error(error)} />
      case "agent-status":
        return <AgentStatusManager onError={(error) => console.error(error)} />
      case "organizations":
        return <OrganizationManager onError={(error) => console.error(error)} onSuccess={(message) => console.log(message)} />
      case "list-id-management":
        return <ListIdManager onError={(error) => console.error(error)} onSuccess={(message) => console.log(message)} />
      case "vendor-intake":
        return <FMOVendorIntakeForms />
      case "form-assignments":
        return <FMOVendorFormAssignment />
      case "load-balancer":
        return <FMOLoadBalancer />
      case "global-sales":
        return <FMOGlobalSalesReports />
      case "global-pay":
        return <FMOGlobalPayReports />
      default:
        return (
          <div className="card">
            <h2 className="text-xl font-semibold text-accent-900 mb-4">{activeTab.replace("-", " ").toUpperCase()}</h2>
            <div className="bg-accent-50 p-4 rounded-lg">
              <p className="text-sm text-accent-700">
                {activeTab.replace("-", " ")} functionality will be implemented here.
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <Layout title="FMO CEO Dashboard" onMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-0 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar Navigation - Fixed and Responsive */}
        <div
          className={`fixed top-0 sm:top-16 left-0 w-72 h-screen sm:h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-300 p-6 z-10
            ${mobileSidebarOpen ? "block" : "hidden"} lg:block`}
        >
          {/* Mobile header space */}
          <div className="h-14 sm:hidden"></div>
          {/* Campaign Selection Indicator */}
          {isCampaignSelected && (
            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-xl shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Active Campaign</span>
              </div>
              <p className="text-sm text-emerald-700 font-semibold mb-3 truncate">
                {selectedCampaign?.name}
              </p>
              <button
                onClick={() => setSelectedCampaign(null)}
                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium transition-colors"
              >
                Clear Selection
              </button>
            </div>
          )}


          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              // Quality control, HR Account Management, and Referrals don't require campaign selection
              const isDisabled = !["campaign-selector", "add-campaign", "view-dashboard", "hr-account-management", "referrals", "application-data-logs"].includes(item.id) && !isCampaignSelected
              const isActive = activeTab === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (!isDisabled) {
                      handleTabChange(item.id)
                      setMobileSidebarOpen(false) // Close sidebar on item click
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${isActive
                    ? "bg-blue-600 text-white shadow-md"
                    : isDisabled
                      ? "bg-slate-50 text-slate-400 cursor-not-allowed opacity-60"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  disabled={isDisabled}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-white" : isDisabled ? "text-slate-400" : "text-slate-500"
                    }`} />
                  <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                  {isDisabled && (
                    <span className="text-xs text-gray-400 ml-auto flex-shrink-0">(Select Campaign)</span>
                  )}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Main Content Area - Scrolls independently and adjusts for sidebar */}
        <div className="flex-1 lg:ml-72 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-14 sm:pt-16">{renderContent()}</div>
      </div>

    </Layout>
  )
}
